import { getOrgConfig } from '@api/organization';
import { isModuleLocked } from '@constants/lockedModules';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { useQuery } from '@tanstack/react-query';
import type { LockedModuleKey } from '@diagon-alley/common';
import { useCallback } from 'react';

/**
 * The modules outside the signed-in brand's plan, off the org config the sidebar already
 * fetches (same query key, so one request serves both). While the config is loading, or if it
 * fails, nothing is locked: a brand nobody configured sees everything, and an API hiccup must
 * not take screens away from the brands that pay for them.
 */
export function useLockedModules(): {
    lockedModules: LockedModuleKey[];
    isLoading: boolean;
    isLocked: (module: LockedModuleKey | null | undefined) => boolean;
} {
    const { currentUser } = useCurrentUser();
    const organizationId = currentUser?.organizationId ?? null;

    const { data: orgConfig, isLoading } = useQuery({
        queryKey: ['org-config', organizationId],
        queryFn: () => getOrgConfig(organizationId!),
        enabled: !!organizationId,
        staleTime: 5 * 60 * 1000,
    });

    const lockedModules = (orgConfig?.lockedModules ?? []) as LockedModuleKey[];
    const isLocked = useCallback(
        (module: LockedModuleKey | null | undefined) => isModuleLocked(lockedModules, module),
        // The array is rebuilt per render but its contents only change with the org config.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [lockedModules.join('|')],
    );

    return { lockedModules, isLoading: !!organizationId && isLoading, isLocked };
}
